import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import debounce from 'lodash/debounce';
import { withRouter, RouteComponentProps, Link } from 'react-router-dom';
import isUndefined from 'lodash/isUndefined';
import {
  getHotelDetails,
  IHotelDetailsState,
  IRoomsSearchState,
  ITrustYouReviewState,
} from 'src/store/slices/index';
import { RootState } from 'src/store';
import { UrlUtils, Responsive, formatRate, renderNumber, Map } from 'src/utils/index';
import { SESSION_KEY_LABEL } from 'src/constants/index';
import { ISessionKey, IHotelDetailsParams, IAnchor } from 'src/common-types/index';
import { Routes } from 'src/constants/index';
import { CloseBlueSvg, TrustYouSVG } from 'src/assets/index';
import { TrustYouReview, MobileSlider } from 'src/components/components/index';
import MapMarker from 'src/assets/images/map-bed-marker.svg';
import { HotelInfo } from '../hotel-info';
import { HotelImagesDescription } from '../hotel-images-description';
import { Amenities } from '../amenities';
import { Policies } from '../policies';
import { Location } from '../location';
import { SkeletonComponent } from '../skeleton';
import { RoomsSearch } from '../rooms-search';
import { MapWrapper } from '../../map';
import { HotelDetailsServerError } from '../hotel-details-server-error';
import { OtherImportantInformation } from '../other-important-information';
import './style.scss';

interface IMapStateToProps {
  hotelDetailsStore: IHotelDetailsState;
  roomsSearchStore: IRoomsSearchState;
  trustYouReviewStore: ITrustYouReviewState;
}

interface IMapDispatchToProps {
  getHotelDetails: (
    numberId: number,
    sessionKey: ISessionKey | undefined,
    isFromAdminPage: boolean,
    getDetailsData?: () => IHotelDetailsParams,
  ) => void;
}
interface IParams {
  id: string;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, RouteComponentProps<IParams> {
  isFromAdminPage: boolean;
}

interface IState {
  active: number;
  isMapModalOpen: boolean;
}

const ZERO = 0;
const SECOND_ELEMENT = 2;
const DEFAULT_ANCHOR = 0;
const TAB_POLICIES = 'tab.policies';
const TAB_REVIEWS = 'tab.reviews';
const TAB_OTHER_IMPORTANT_INFO = 'other.important.information';
const BIG_MAP_ZOOM = 15;
const DEBOUNCE_TIME = 100;

class HotelDetailsWrapperComponent extends React.Component<IProps, IState> {
  state: IState = {
    active: DEFAULT_ANCHOR,
    isMapModalOpen: false,
  };
  overviewRef: React.RefObject<HTMLDivElement> = React.createRef();
  roomsRef: React.RefObject<HTMLDivElement> = React.createRef();
  amenitiesRef: React.RefObject<HTMLDivElement> = React.createRef();
  locationRef: React.RefObject<HTMLDivElement> = React.createRef();
  policiesRef: React.RefObject<HTMLDivElement> = React.createRef();
  otherImportantRef: React.RefObject<HTMLDivElement> = React.createRef();
  reviewsRef: React.RefObject<HTMLDivElement> = React.createRef();

  ITEMS_ANCHOR: IAnchor[] = [
    { id: 'tab.overview', ref: this.overviewRef },
    { id: 'rooms', ref: this.roomsRef },
    { id: 'tab.amenities', ref: this.amenitiesRef },
    { id: 'tab.location', ref: this.locationRef },
    { id: 'tab.policies', ref: this.policiesRef },
    { id: 'other.important.information', ref: this.otherImportantRef },
    { id: 'tab.reviews', ref: this.reviewsRef },
  ];

  scrollView = (e: React.MouseEvent<HTMLParagraphElement>, ref: React.RefObject<HTMLElement>) => {
    this.setState({ active: +e.currentTarget.dataset.index });
    ref.current.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'end' });
  };

  getAnchors = (): IAnchor[] => {
    const { hotel } = this.props.hotelDetailsStore;
    const { reviewModule } = this.props.trustYouReviewStore;

    const allDataPolicies = [
      ...(hotel.hotelDetails.checkInInformation || []),
      ...(hotel.hotelDetails.checkOutInformation || []),
      ...(hotel.hotelDetails.pets || []),
    ];

    return this.ITEMS_ANCHOR.filter((item) => {
      if (
        (item.id === TAB_POLICIES && allDataPolicies.length === ZERO) ||
        (item.id === TAB_OTHER_IMPORTANT_INFO &&
          hotel.hotelDetails.otherImportantInformation.length === ZERO) ||
        (item.id === TAB_REVIEWS && (isUndefined(reviewModule) || !reviewModule?.summary))
      ) {
        return;
      }
      return item;
    });
  };

  filtersAnchor = () => {
    return this.getAnchors().map((itemAnchor, i) => (
      <p
        key={i}
        className={`hotel-details-wrapper__tab-anchors ${i === this.state.active ? 'active' : ''}`}
        onClick={(e) => this.scrollView(e, itemAnchor.ref)}
        data-index={i}
      >
        <FormattedMessage id={itemAnchor.id} />
      </p>
    ));
  };

  onClickScrollToRooms = (e: React.MouseEvent<HTMLDivElement>) => {
    this.scrollView(e, this.roomsRef);
  };

  onClickScrollToReviews = (e: React.MouseEvent<HTMLDivElement>) => {
    this.scrollView(e, this.reviewsRef);
  };

  getDetailsData = (): IHotelDetailsParams => {
    const { search } = this.props.history.location;
    const checkIn = new URLSearchParams(search).get('checkIn');
    const checkOut = new URLSearchParams(search).get('checkOut');

    return {
      checkIn,
      checkOut,
    };
  };

  componentDidMount() {
    const { isFromAdminPage, getHotelDetails } = this.props;
    const hotelId =
      Number(this.props.history.location.pathname.split('/')[SECOND_ELEMENT]) ||
      Number(this.props.match.params.id);
    const values = UrlUtils.getValues();

    getHotelDetails(
      hotelId,
      values[SESSION_KEY_LABEL] as ISessionKey,
      isFromAdminPage,
      this.getDetailsData,
    );

    window.addEventListener('scroll', this.onScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScroll);
  }

  getActiveAnchor = (): IAnchor => {
    const anchors: IAnchor[] = this.getAnchors();
    const { pageYOffset } = window;
    let activeRef: IAnchor = anchors[ZERO];

    anchors.forEach((anchor: IAnchor) => {
      const { ref } = anchor;

      if (ref && ref.current) {
        const { current } = ref;
        const height = current.getBoundingClientRect()?.height;
        const offsetTop = current.offsetTop;
        const offsetBottom = offsetTop + height;

        if (height && offsetTop && pageYOffset >= offsetTop && pageYOffset <= offsetBottom) {
          activeRef = anchor;
        }
      }
    });

    return activeRef;
  };

  onScroll = debounce(() => {
    const { hotel, loading } = this.props.hotelDetailsStore;

    if (hotel && !loading) {
      const anchor = this.getActiveAnchor();

      if (anchor && anchor.id) {
        const index = this.getAnchors().findIndex(({ id }) => id === anchor.id);

        if (index >= ZERO && this.state.active !== index) {
          this.setState({ active: index });
        }
      }
    }
  }, DEBOUNCE_TIME);

  showMap = () => {
    this.setState({ isMapModalOpen: true });
    document.body.style.overflow = 'hidden';
  };

  closeMap = () => {
    this.setState({ isMapModalOpen: false });
    document.body.style.overflow = 'initial';
  };

  render(): React.ReactNode {
    const { isFromAdminPage } = this.props;
    const { isMapModalOpen } = this.state;
    const { hotel, loading, isServerError } = this.props.hotelDetailsStore;
    const { reviewModule } = this.props.trustYouReviewStore;

    return (
      <div className="hotel-details-wrapper">
        {isServerError ? (
          <HotelDetailsServerError />
        ) : (
          <>
            <div className="hotel-details-wrapper__back-link desktop">
              {!isFromAdminPage && (
                <Link to={`${Routes.Search}${location.search}`}>
                  <FormattedMessage id="back.to.list" />
                </Link>
              )}
            </div>
            {loading ? <SkeletonComponent /> : null}
            {hotel && !loading ? (
              <div className="hotel-details-wrapper__wrapper">
                <div ref={this.overviewRef} />
                <div className="hotel-details-wrapper__back-link mobile">
                  {!isFromAdminPage && (
                    <Link to={`${Routes.Search}${location.search}`}>
                      <FormattedMessage id="back.to.list" />
                    </Link>
                  )}
                </div>
                <div className="hotel-details-wrapper__desktop-hotel-info">
                  <HotelInfo showBigMap={this.showMap} hotel={hotel.hotelDetails} />
                </div>
                <MobileSlider images={hotel.hotelDetails.images || []} />
                <div className="hotel-details-wrapper__details-anchor">
                  {hotel.hotelDetails && this.filtersAnchor()}
                </div>
                <div className="hotel-details-wrapper__mobile-hotel-info">
                  <HotelInfo hotel={hotel.hotelDetails} showBigMap={this.showMap} />
                </div>
                <HotelImagesDescription
                  images={hotel.hotelDetails.images || []}
                  hotelName={hotel.hotelDetails.title}
                  hotelDescription={hotel.hotelDetails.descriptions || []}
                  additionalDescriptions={hotel.hotelDetails.additionalDescriptions || []}
                  hotelRooms={hotel.hotelDetails.roomsCount}
                  onClickScrollToRooms={this.onClickScrollToRooms}
                  reviewModule={reviewModule}
                  onClickScrollToReviews={this.onClickScrollToReviews}
                />
                {(!!hotel.hotelDetails.descriptions.length ||
                  !!hotel.hotelDetails.additionalDescriptions.length) &&
                !isUndefined(reviewModule) &&
                reviewModule?.summary ? (
                  <div className="hotel-details-wrapper__trust-you-info-wrapper">
                    <div className="hotel-details-wrapper__trust-you-info">
                      <div className="hotel-details-wrapper__trust-you-icon">
                        <TrustYouSVG />
                      </div>
                      <p className="hotel-details-wrapper__trust-you-text">
                        <span className="hotel-details-wrapper__bold">
                          {reviewModule && formatRate(reviewModule.summary?.score)}
                        </span>
                        (
                        {reviewModule && reviewModule.reviews_count
                          ? renderNumber(reviewModule.reviews_count)
                          : null}{' '}
                        <FormattedMessage id="reviews" />)
                      </p>
                    </div>
                    {reviewModule ? (
                      <span
                        className="hotel-details-wrapper__link-text"
                        onClick={this.onClickScrollToReviews}
                      >
                        <FormattedMessage id="view.all" />
                      </span>
                    ) : null}
                  </div>
                ) : null}
                <Responsive>
                  <RoomsSearch
                    isFromAdminPage={isFromAdminPage}
                    hotel={hotel}
                    refAnchor={this.roomsRef}
                  />
                </Responsive>
                <Amenities hotel={hotel.hotelDetails} refAnchor={this.amenitiesRef} />
                <Location
                  hotel={hotel.hotelDetails}
                  refAnchor={this.locationRef}
                  showBigMap={this.showMap}
                />
                <Policies hotel={hotel.hotelDetails} refAnchor={this.policiesRef} />
                <OtherImportantInformation
                  hotel={hotel.hotelDetails}
                  refAnchor={this.otherImportantRef}
                />
                <TrustYouReview refAnchor={this.reviewsRef} />
                <div
                  className="hotel-details-wrapper__big-map-wrapper"
                  style={{ display: isMapModalOpen ? 'block' : 'none' }}
                >
                  <div className="hotel-details-wrapper__big-map-top-section">
                    <span className="hotel-details-wrapper__big-map-close" onClick={this.closeMap}>
                      <CloseBlueSvg />
                    </span>
                    {hotel.hotelDetails.title}
                  </div>
                  <MapWrapper
                    locations={[
                      { location: hotel.hotelDetails.location, id: hotel.hotelDetails.id },
                    ]}
                    center={Map.getGoogleLocation(hotel.hotelDetails.location)}
                    icon={MapMarker}
                    mapOptions={{ zoom: BIG_MAP_ZOOM, disableDefaultUI: true }}
                  />
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    hotelDetailsStore: state.hotelDetailsStore,
    roomsSearchStore: state.roomsSearchStore,
    trustYouReviewStore: state.trustYouReviewStore,
  };
};

const mapDispatchToProps: IMapDispatchToProps = { getHotelDetails };

export const HotelDetailsWrapper = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(HotelDetailsWrapperComponent));

